import React, { useState } from 'react';
import { Crosshair, LineChart, CheckSquare, Sparkles, AlertCircle, RefreshCw } from 'lucide-react';
import { CustomProject } from '../../types/productBuilder';
import { RadarRecommendation } from '../types';

interface RadarProps {
  project: CustomProject;
  onUpdateProject: (p: CustomProject) => void;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const Radar: React.FC<RadarProps> = ({ project, onUpdateProject, onAddLog }) => {
  const [providerConfigured, setProviderConfigured] = useState<boolean>(false);
  const [fetching, setFetching] = useState<boolean>(false);
  const [sourceDetails, setSourceDetails] = useState<string>("None — Pending Integration");

  const getRadarRecommendations = (): RadarRecommendation[] => {
    // Only return general educational templates
    return [
      {
        id: 'rad_gen_1',
        title: 'Static Educational Trend: Localized Page Sections',
        impact: 'MEDIUM',
        horizon: 'NEXT_MONTH',
        description: 'Educational Model: Standard web telemetry suggestions for organizing structural sections based on common user interaction paths.',
        actionableDraft: 'Localized Content Feature Grid'
      },
      {
        id: 'rad_gen_2',
        title: 'Static Educational Trend: Security Best Practices',
        impact: 'HIGH',
        horizon: 'NEXT_QUARTER',
        description: 'Educational Model: Incorporating prominent disclaimers and localized privacy settings placeholder component blocks.',
        actionableDraft: 'Privacy Policy Consent Drawer'
      }
    ];
  };

  const applyDraft = (rec: RadarRecommendation) => {
    if (!rec.actionableDraft || project.pages.length === 0) return;

    const firstPage = project.pages[0];
    const items = [...firstPage.layoutSections];

    if (items.includes(rec.actionableDraft)) {
      alert(`The component "${rec.actionableDraft}" is already present in your layout.`);
      return;
    }

    items.push(rec.actionableDraft);
    const updatedPages = project.pages.map((p, idx) => 
      idx === 0 ? { ...p, layoutSections: items } : p
    );

    onUpdateProject({ ...project, pages: updatedPages });
    onAddLog('Product Builder', `Drafted educational template row: "${rec.actionableDraft}"`, 'SUCCESS', 'Injected planning layout node.');
    alert(`Success: Instantiated educational preview block "${rec.actionableDraft}" inside Sandbox.`);
  };

  const currentRecommend = getRadarRecommendations();

  return (
    <div className="npx-builder-radar-container space-y-6 text-left animate-fade-in">
      <div className="border-b border-white/5 pb-4">
        <div className="flex items-center gap-1.5">
          <Crosshair className="w-4 h-4 text-[#00ffff]" />
          <h2 className="text-sm font-mono tracking-widest text-[#00ffff] uppercase font-black">6. Future Radar Guidance</h2>
        </div>
        <p className="text-xs text-zinc-400 mt-1">Audit active industry design layouts and reference standard web planning layouts under connected regulatory feeds.</p>
      </div>

      {/* Connection Indicator Alert */}
      <div className="bg-zinc-950 border border-white/5 p-5 rounded-2xl space-y-4">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div className="space-y-1">
            <span className="text-[10px] font-mono text-[#00ffff] uppercase block font-black">Feed Configuration Status</span>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-xs font-mono font-bold text-amber-500 uppercase">Future Radar source connection required</span>
            </div>
            <p className="text-[10px] text-zinc-550 font-sans normal-case">
              Source Details: <span className="font-mono text-zinc-400 font-bold">{sourceDetails}</span>
            </p>
          </div>
          <button 
            disabled 
            className="px-3 py-1.5 rounded-xl bg-zinc-900 border border-white/5 text-[9px] font-mono font-bold uppercase text-zinc-500 cursor-not-allowed"
          >
            Connect API Feed
          </button>
        </div>

        <div className="p-3.5 bg-amber-500/5 rounded-xl border border-amber-500/10 flex gap-2.5 items-start text-amber-200">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <p className="text-[10px] font-sans leading-normal normal-case text-amber-300">
            Live AI market forecast capabilities are suspended as no approved third-party provider or manual administrative source feed has been connected yet. The recommendations listed below are standard static educational templates only.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {currentRecommend.map((rec) => (
          <div
            key={rec.id}
            className="p-5 rounded-2xl bg-black/40 border border-white/5 flex flex-col justify-between items-start text-left"
          >
            <div className="space-y-3 font-mono w-full">
              <div className="flex justify-between items-center text-[8px] font-extrabold uppercase">
                <span className="px-2 py-0.5 rounded leading-none bg-yellow-500/10 text-yellow-500">
                  IMPACT: {rec.impact}
                </span>
                <span className="text-purple-400 font-bold border border-purple-500/20 bg-purple-950/20 px-1.5 py-0.5 rounded">
                  {rec.horizon.replace('_', ' ')}
                </span>
              </div>
              <h3 className="text-xs font-bold text-white uppercase tracking-wider">{rec.title}</h3>
              <p className="text-[10px] text-zinc-400 font-sans normal-case leading-relaxed">{rec.description}</p>
            </div>

            {rec.actionableDraft && (
              <button
                onClick={() => applyDraft(rec)}
                className="mt-6 w-full font-mono text-[9px] font-black uppercase text-[#020202] hover:text-white bg-gradient-to-r from-cyan-400 to-purple-500 py-2 rounded-xl text-center cursor-pointer transition-colors hover:from-cyan-900 hover:to-indigo-950 border border-cyan-500/10 flex items-center justify-center gap-1"
              >
                <CheckSquare size={11} className="text-current" />
                <span>Inject Educational Block</span>
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="p-4 bg-zinc-950/40 border border-white/5 rounded-2xl flex items-start gap-3">
        <LineChart size={15} className="text-purple-400 shrink-0 mt-0.5" />
        <div className="space-y-1">
          <h4 className="text-[11px] font-mono text-white uppercase font-bold">Educational Planning Standards</h4>
          <p className="text-[9.5px] text-zinc-500 leading-relaxed font-sans normal-case">
            Future Radar supports localized system planning configurations safely. To connect active external research feeds, please contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
};
